import ReactMarkdown, {
	defaultUrlTransform,
} from "react-markdown";
import rehypeHighlight from "rehype-highlight";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";

type TMarkdownRendererProps = {
	content: string;
	className?: string;
};

export default function MarkdownRenderer({
	content,
	className = "",
}: TMarkdownRendererProps) {
	const urlTransform = (url: string) =>
		url.startsWith("data:")
			? url
			: defaultUrlTransform(url);

	return (
		<div className={`markdown-content ${className}`}>
			<ReactMarkdown
				urlTransform={urlTransform}
				remarkPlugins={[remarkGfm]}
				rehypePlugins={[rehypeHighlight, rehypeRaw]}
				components={{
					ul: ({ children }) => (
						<ul className="markdown-ul">{children}</ul>
					),
					ol: ({ children }) => (
						<ol className="markdown-ol">{children}</ol>
					),
					li: ({ children }) => (
						<li className="markdown-li">{children}</li>
					),
					blockquote: ({ children }) => (
						<blockquote className="markdown-blockquote">
							{children}
						</blockquote>
					),
					table: ({ children }) => (
						<div
							className="markdown-table-wrapper"
							style={{ maxWidth: "100%" }}
						>
							<table className="markdown-table">
								{children}
							</table>
						</div>
					),
					thead: ({ children }) => (
						<thead className="markdown-thead">
							{children}
						</thead>
					),
					tbody: ({ children }) => (
						<tbody className="markdown-tbody">
							{children}
						</tbody>
					),
					tr: ({ children }) => (
						<tr className="markdown-tr">{children}</tr>
					),
					th: ({ children }) => (
						<th className="markdown-th">{children}</th>
					),
					td: ({ children }) => (
						<td className="markdown-td">{children}</td>
					),
					a: ({ href, children }) => (
						<a
							href={href}
							className="markdown-a"
							target="_blank"
							rel="noopener noreferrer"
						>
							{children}
						</a>
					),
					strong: ({ children }) => (
						<strong className="markdown-strong">
							{children}
						</strong>
					),
					em: ({ children }) => (
						<em className="markdown-em">{children}</em>
					),
					hr: () => <hr className="markdown-hr" />,
					pre: ({ children }) => (
						<pre className="markdown-pre">{children}</pre>
					),
					code: ({ children, className: codeClassName, ...props }) => {
						const isInline = !codeClassName;
						if (isInline) {
							return (
								<code className="markdown-inline-code" {...props}>
									{children}
								</code>
							);
						}
						return (
							<code className={codeClassName} {...props}>
								{children}
							</code>
						);
					},
				}}
			>
				{content}
			</ReactMarkdown>
		</div>
	);
}
